import {
	ClientFeatures,
	DegreeLevelLabel,
	DegreeLevelOption,
} from '@uc/web/shared/data-models';
import { DEGREE_LEVEL_OPTIONS } from '../static/courses-hub.properties';
import { POSTGRADUATE_DEGREE_LEVELS } from '../static/courses-filter.properties';

export const getFeatureKey = (str: string) => {
	return (str?.charAt(0).toUpperCase() + str?.slice(1) ||
		'') as keyof typeof ClientFeatures;
};

export const getDegreeLevelOptions = (labels: DegreeLevelLabel[]) => {
	return labels.reduce<DegreeLevelOption[]>((acc, label) => {
		const matchingOptions = DEGREE_LEVEL_OPTIONS.filter(
			(option) => option.label === label,
		);
		return acc.concat(matchingOptions);
	}, []);
};

export const isPostgraduate = (degreeLevel: string | undefined) => {
	return !!POSTGRADUATE_DEGREE_LEVELS.find((degree) => degree.link === degreeLevel);
};

export const formatTitle = (
	title: string | undefined,
	subject: string | undefined,
	location?: string | undefined,
): string | undefined => {
	if (!title || !subject) return;

	const defaultLocation = 'UK';
	const _title = location ? title.replace(/the /, '').replace(',', '') : title;

	const formattedSubject = formatTitleCaseString(subject);
	const formattedLocation = location
		? formatTitleCaseString(location)
		: defaultLocation;
	const formattedLocation_1 = location ? `in ${formattedLocation}` : '';

	return _title
		.replace('[SUBJECT]', formattedSubject)
		.replace('[LOCATION]', formattedLocation)
		.replace('[LOCATION_1]', formattedLocation_1);
};

export const formatMetaDescription = (
	description: string | undefined,
	subject: string | undefined,
	courseCount: number | string,
	uniCount: number | string,
	location: string | undefined,
): string | undefined => {
	if (!description || !subject) return;

	const formattedSubject = subject.replace(/-/g, ' ');

	const formattedLocation = location && formatTitleCaseString(location);

	const locationPositionOne = formattedLocation ?? 'the UK';
	const locationPositionTwo = formattedLocation ?? 'UK';

	const formattedDescription = description
		.replace(/\[SUBJECT\]/g, formattedSubject)
		.replace('[LOCATION]', locationPositionOne)
		.replace('[LOCATION]', locationPositionTwo)
		.replace('[COURSE_COUNT]', courseCount.toString())
		.replace('[UNIVERSITY_COUNT]', uniCount.toString());

	return formattedDescription;
};

export const formatHeaderDescription = (
	description: string | undefined,
	subject: string | undefined,
	courseCount: number | string,
	uniCount: number | string,
	location: string | undefined,
): string | undefined => {
	if (!description || !subject) return;

	const defaultLocation = 'UK universities';
	const formattedLocation = location
		? `universities in ${formatTitleCaseString(location)}`
		: defaultLocation;

	const formattedSubject = subject.replace(/-/g, ' ');

	const formattedDescription = description
		.replace(/\[SUBJECT\]/g, formattedSubject)
		.replace('[LOCATION]', formattedLocation)
		.replace('[COURSE_COUNT]', courseCount.toString())
		.replace('[UNIVERSITY_COUNT]', uniCount.toString());

	return formattedDescription;
};

export const formatSearchTerm = (str: string) =>
	str?.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()) || '';

export const formatTitleCaseString = (str: string) =>
	str
		?.replace(/-/g, ' ')
		.replace(/\b\w/g, (char) => char.toUpperCase())
		.replace(/\b(To|And|The|Of)\b/g, (match) => match.toLowerCase()) || '';
